import React, { useCallback, useMemo } from "react";

import { format } from "date-fns";

import useAdvisorClientsV2 from "@fartherfinance/frontend/api/Entity/hooks/useAdvisorClientsV2";
import { Client } from "@fartherfinance/frontend/api/Entity/requests/getAdvisorClients";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { unknownBalanceValue } from "@src/constants/unknownBalanceValue";
import { useTableDataWithAdvisorNames } from "@src/multiCustodian/hooks/Advisor/HouseholdClients/useTableDataWithAdvisorNames";
import { useAdvisorPageInit } from "@src/multiCustodian/hooks/Advisor/useAdvisorPageInit";
import { useTableRows } from "@src/multiCustodian/hooks/Advisor/useTableRows";
import { toPercentage } from "@src/multiCustodian/utils/commonFunctions";
import { consecutiveSpaces } from "@src/regex";
import FullDataTable from "@src/sharedComponents/Table/FullDataTable";
import { Cell, Row } from "@src/sharedComponents/Table/Types";

import { clientTableColumnDefs, createDisplayDataRow } from "./columnDefs";
import EmptyButton from "./EmptyButton";
import {
  ClientsTableProps,
  ClientWithAdvisorName,
  TableKeys,
  TableKeysType,
} from "./types";

import styles from "./ClientsTable.module.css";

const ClientsTable: React.FC<ClientsTableProps> = ({
  search,
  searchFilters,
}) => {
  const { history, auth, advisorId } = useAdvisorPageInit();

  const advisorClients = useAdvisorClientsV2(advisorId!, auth);

  const openClientDetails = useCallback(
    (clientId: ClientId) => {
      const location = history.location;
      history.push({ ...location, pathname: `/Advisor/Clients/${clientId}` });
    },
    [history]
  );

  // Enrich Client Data with Advisor Names
  const tableDataWithAdvisorNames = useTableDataWithAdvisorNames({
    tableData: advisorClients.data?.clients as Client[],
    auth,
    componentName: "ClientsTable",
  });

  // Filter Data and Create Rows
  const searchedData = useMemo(() => {
    const filteredMapped = tableDataWithAdvisorNames.data
      .filter((c) => {
        const lowercaseSearch = search
          .toLowerCase()
          .replace(consecutiveSpaces, " ");

        const clientFullName = `${c.name.first.toLowerCase()} ${c.name.last.toLowerCase()}`;
        const advisorFullName = `${c.advisorName.first.toLowerCase()} ${c.advisorName.last.toLowerCase()}`;

        // const _checkFormattedHouseholdName = () => {
        //   if (!c.householdMemberships?.length) {
        //     return "".includes(lowercaseSearch);
        //   }
        //   return c.householdMemberships.some((household) =>
        //     household.householdName.toLowerCase().includes(lowercaseSearch)
        //   );
        // };

        if (
          searchFilters.Name.checked &&
          clientFullName.includes(lowercaseSearch)
        ) {
          return true;
        }

        if (
          searchFilters.Email.checked &&
          c.emailAddress.toLowerCase().includes(lowercaseSearch)
        ) {
          return true;
        }

        if (
          searchFilters.Mobile.checked &&
          (c.phoneNumber ?? unknownBalanceValue)?.includes(search)
        ) {
          return true;
        }

        if (searchFilters["Client ID"].checked && c.clientId.includes(search)) {
          return true;
        }

        if (
          searchFilters.Advisor.checked &&
          advisorFullName.includes(lowercaseSearch)
        ) {
          return true;
        }

        if (
          searchFilters.Created.checked &&
          format(c.registeredTime, "M/d/yyyy").includes(search)
        ) {
          return true;
        }

        return false;
      })
      .map(
        (c: ClientWithAdvisorName): Row<TableKeysType, Cell> =>
          createDisplayDataRow(c, openClientDetails)
      );

    return filteredMapped;
  }, [
    tableDataWithAdvisorNames.data,
    openClientDetails,
    search,
    searchFilters,
  ]);

  // Render Loading/Empty Rows
  const rows = useTableRows({
    searchedData,
    columnDefinitions: clientTableColumnDefs,
    isLoading: advisorClients.isLoading || tableDataWithAdvisorNames.isLoading,
  });

  if (advisorClients.hasError || tableDataWithAdvisorNames.hasError) {
    return (
      <div className={styles.errorContainer}>Error loading client list</div>
    );
  }

  return (
    <FullDataTable
      isLoading={
        advisorClients.isLoading || tableDataWithAdvisorNames.isLoading
      }
      columns={TableKeys}
      rows={rows}
      disableColumnSorting={
        searchedData.length > 0 ? ["Login", "Mobile"] : TableKeys
      }
      defaultRowsPerPage={10}
      defaultSortColumn={["Created", "desc"]}
      emptyCell={<EmptyButton />} // This is the highest cell we will have
    >
      <colgroup>
        {TableKeys.slice(0, -1).map((key) => (
          <col key={key} width={toPercentage(90 / 6)} />
        ))}
        <col width="10%" />
      </colgroup>
    </FullDataTable>
  );
};

export default ClientsTable;
